import { useEffect, useState, useCallback } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { apiAdmin, buildFileUrl, formatApiError } from "@/lib/api";
import { resolveActivePlaylist } from "@/lib/schedule";
import { Tv, Plus, ListMusic, Trash2, Copy, Eye, EyeOff, KeyRound, LogOut, Clock, X, Play, Search, ChevronLeft, ChevronRight, ChevronDown, RefreshCw, ArrowRightLeft, Upload, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaylistPreviewDialog from "@/components/PlaylistPreviewDialog";
import TVLivePreview from "@/components/TVLivePreview";
import NYClock from "@/components/NYClock";
import QuickContentDialog from "@/components/QuickContentDialog";
import RenameTVDialog from "@/components/RenameTVDialog";
import AccountCodeBanner from "@/components/AccountCodeBanner";
import { toast } from "sonner";

export default function TVsPage() {
  const { activeBusiness, businesses } = useBusiness();
  const [tvs, setTvs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [nowPlaying, setNowPlaying] = useState({}); // tvId -> {status, media, ...}
  const [pendingDevices, setPendingDevices] = useState([]);

  // Dialogs state
  const [newTvOpen, setNewTvOpen] = useState(false);
  const [createPlOpen, setCreatePlOpen] = useState(false);
  const [createPlForTv, setCreatePlForTv] = useState(null);
  const [credsTv, setCredsTv] = useState(null);
  const [scheduleTv, setScheduleTv] = useState(null);
  const [quickContentTv, setQuickContentTv] = useState(null);
  const [previewPlaylist, setPreviewPlaylist] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expandedTvId, setExpandedTvId] = useState(null);
  const [claimingDevice, setClaimingDevice] = useState(null); // device row
  const [movingDevice, setMovingDevice] = useState(null);     // tv row to move
  const [renamingTv, setRenamingTv] = useState(null);
  const [tvsLoaded, setTvsLoaded] = useState(false);
  const [, setNowTick] = useState(0);
  const PAGE_SIZE = 24;

  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Poll pending devices every 5s — admin watches new Fire Sticks pop in live
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await apiAdmin.get("/devices/pending");
        if (!cancelled) setPendingDevices(data || []);
      } catch {
        /* ignore */
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const load = useCallback(async () => {
    if (!activeBusiness) return;
    const [tvRes, plRes, mRes] = await Promise.all([
      apiAdmin.get(`/tvs?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/playlists?business_id=${activeBusiness.id}`),
      apiAdmin.get(`/media?business_id=${activeBusiness.id}`),
    ]);
    setTvs(tvRes.data);
    setTvsLoaded(true);
    setPlaylists(plRes.data);
    setMedia(mRes.data);
  }, [activeBusiness]);

  useEffect(() => {
    load();
  }, [load]);

  // Live preview polling — ONE bulk request for all TVs (scales to hundreds)
  useEffect(() => {
    if (!activeBusiness) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await apiAdmin.get(`/tvs/now-playing-bulk?business_id=${activeBusiness.id}`);
        if (cancelled) return;
        const next = {};
        data.forEach((item) => { next[item.tv_id] = item; });
        setNowPlaying(next);
      } catch {
        /* ignore transient failures */
      }
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeBusiness]);

  const assignPlaylist = async (tvId, playlistId) => {
    try {
      await apiAdmin.post(`/tvs/${tvId}/assign-playlist/${playlistId}`);
      toast.success("Playlist assigned");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Resolve which playlist a TV is REALLY playing right now. Prefers the
  // server's truth (from the bulk endpoint) and falls back to client-side
  // resolution when the bulk request hasn't responded yet.
  const resolveActive = useCallback(
    (tv) => {
      const np = nowPlaying[tv.id];
      if (np?.active_playlist_id) {
        const pl = playlists.find((p) => p.id === np.active_playlist_id);
        if (pl) return pl;
      }
      return resolveActivePlaylist(tv, playlists);
    },
    [playlists, nowPlaying]
  );

  const deleteTv = async (tvId) => {
    try {
      await apiAdmin.delete(`/tvs/${tvId}`);
      toast.success("TV deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const signOutTv = async (tvId) => {
    try {
      await apiAdmin.post(`/tvs/${tvId}/sign-out`);
      toast.success("TV signed out — Fire Stick will return to login");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refreshTv = async (tvId, tvName) => {
    try {
      await apiAdmin.post(`/tvs/${tvId}/refresh`);
      toast.success(`Refresh sent to ${tvName || "TV"} — the screen will reload in seconds`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refreshAllTvs = async () => {
    if (!tvs.length) return;
    try {
      await Promise.all(tvs.map((t) => apiAdmin.post(`/tvs/${t.id}/refresh`).catch(() => {})));
      toast.success(`Refresh sent to all ${tvs.length} TVs`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearTvCache = async (tvId, tvName) => {
    if (!window.confirm(`Wipe local media cache on "${tvName || "TV"}" and reload it?\n\nThe Fire Stick will re-download all videos/images from Supabase on next play.`)) return;
    try {
      await apiAdmin.post(`/tvs/${tvId}/clear-cache`);
      toast.success(`Cache wipe sent to ${tvName || "TV"} — it will reload in seconds`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="tvs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Dispositivos</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Devices
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fire Sticks &amp; TVs running CreatiCore at {activeBusiness?.name}. New devices auto-register here when first booted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <NYClock />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.25} />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search device or location"
              className="pl-9 h-10 w-64"
              data-testid="tv-search-input"
            />
          </div>
          <Button onClick={refreshAllTvs} variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="tvs-refresh-all-button" disabled={!tvs.length}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh all
          </Button>
          <Button onClick={() => setNewTvOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="tv-add-button">
            <Plus className="h-4 w-4 mr-2" /> Add device (manual)
          </Button>
        </div>
      </div>

      <AccountCodeBanner />

      {/* Pending devices — minimal, neutral cards (no loud colors) */}
      {pendingDevices.length > 0 && (
        <div className="space-y-3" data-testid="pending-devices-panel">
          <div className="flex items-center gap-2.5 text-xs text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-900" />
            <span className="uppercase tracking-[0.18em] font-semibold text-slate-700">
              {pendingDevices.length} pending {pendingDevices.length === 1 ? "device" : "devices"}
            </span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">Waiting to be claimed</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pendingDevices.map((d) => (
              <div
                key={d.id}
                className="rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all p-4 flex flex-col gap-3"
                data-testid={`pending-device-${d.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-50 border border-slate-200 grid place-items-center shrink-0">
                    <Tv className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-sm text-slate-900 truncate">
                        {d.name || "Unclaimed device"}
                      </div>
                      <span className={`inline-flex items-center gap-1 text-[10px] tabular-nums uppercase tracking-widest font-medium ${
                        d.status === "online" ? "text-emerald-700" : "text-slate-400"
                      }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${d.status === "online" ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {d.status}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono truncate mt-0.5" title={d.device_uid}>
                      {d.device_uid?.slice(0, 18)}…
                    </div>
                    {d.location && (
                      <div className="text-[11px] text-slate-500 truncate mt-0.5">{d.location}</div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setClaimingDevice(d)}
                  className="w-full text-xs font-medium tracking-tight rounded-lg border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white text-slate-700 py-2 transition-colors"
                  data-testid={`claim-device-${d.id}`}
                >
                  Claim device
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const q = search.trim().toLowerCase();
        const filtered = q
          ? tvs.filter(
              (t) =>
                t.name.toLowerCase().includes(q) ||
                t.location.toLowerCase().includes(q) ||
                t.username.toLowerCase().includes(q)
            )
          : tvs;
        const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
        const safePage = Math.min(page, totalPages - 1);
        const pageItems = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
        return (
          <>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                Showing <b className="text-slate-700">{pageItems.length}</b> of {filtered.length}
                {filtered.length !== tvs.length ? ` (${tvs.length} total)` : ""}
              </span>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    data-testid="tv-page-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span>
                    Page <b className="text-slate-700">{safePage + 1}</b> / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="tv-page-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="tvs-table">
              {/* Sticky header row */}
              <div
                className="hidden md:grid items-center gap-4 px-6 py-3 border-b border-slate-100 bg-slate-50/60 text-[11px] uppercase tracking-widest text-slate-500 font-medium"
                style={{ gridTemplateColumns: "minmax(220px,1.6fr) 1fr 1fr 1.2fr 0.9fr 40px" }}
              >
                <div>Television</div>
                <div>Status</div>
                <div>Playlist</div>
                <div>Schedule</div>
                <div>Credentials</div>
                <div></div>
              </div>

              {!tvsLoaded && (
                <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="tvs-loading">
                  Cargando pantallas…
                </div>
              )}

              {tvsLoaded && pageItems.length === 0 && (
                <div className="px-6 py-12 text-center">
                  <div className="font-heading text-lg font-semibold text-slate-900">
                    {tvs.length === 0 ? "No televisions yet" : "No TVs match your search"}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {tvs.length === 0
                      ? "Add your first Fire Stick to get started."
                      : "Try a different keyword."}
                  </p>
                </div>
              )}

              {pageItems.map((tv, idx) => {
                const defaultPl = playlists.find((p) => p.id === tv.playlist_id);
                const activePl = resolveActive(tv);
                const currentPl = activePl || defaultPl; // expanded panel still references the default for editing
                const np = nowPlaying[tv.id];
                const isOnline = np?.status === "online";
                const isExpanded = expandedTvId === tv.id;
                const isLast = idx === pageItems.length - 1;
                return (
                  <div
                    key={tv.id}
                    className={!isLast ? "border-b border-slate-100" : ""}
                    data-testid={`tv-card-${tv.id}`}
                  >
                    {/* Main row */}
                    <div
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setExpandedTvId(isExpanded ? null : tv.id); } }}
                      onClick={() => setExpandedTvId(isExpanded ? null : tv.id)}
                      className={`w-full text-left cursor-pointer px-8 py-7 grid items-center gap-5 transition-colors hover:bg-slate-50/70 ${
                        isExpanded ? "bg-slate-50/70" : ""
                      }`}
                      style={{ gridTemplateColumns: "minmax(280px,1.7fr) 1fr 1.1fr 1.2fr 0.9fr 40px" }}
                      data-testid={`tv-row-${tv.id}`}
                    >
                      {/* Column 1: Preview + name */}
                      <div className="flex items-center gap-5 min-w-0">
                        <div
                          className="w-32 shrink-0 aspect-video rounded-xl overflow-hidden bg-black relative ring-1 ring-slate-100"
                          data-testid={`tv-${tv.id}-preview`}
                        >
                          <TVLivePreview playlist={activePl} media={media} nowPlaying={np} />
                          {isOnline && (
                            <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[8px] font-medium text-red-200 bg-black/55 backdrop-blur px-1 py-0.5 rounded">
                              <span className="h-1 w-1 rounded-full bg-red-500 animate-pulse" />
                              LIVE
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="font-heading text-lg font-semibold text-slate-900 truncate" style={{ letterSpacing: "-0.01em" }} data-testid={`tv-${tv.id}-name`}>
                              {tv.name}
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setRenamingTv(tv); }}
                              className="shrink-0 rounded-md p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                              title="Editar nombre y ubicación"
                              aria-label="Editar nombre y ubicación"
                              data-testid={`tv-${tv.id}-rename-btn`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="text-sm text-slate-500 truncate mt-1">
                            {tv.location}{tv.username && <> · <span className="font-mono">@{tv.username}</span></>}
                          </div>
                        </div>
                      </div>

                      {/* Column 2: Status */}
                      <div className="hidden md:block">
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                            isOnline
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-50 text-slate-500"
                          }`}
                          data-testid={`tv-${tv.id}-status`}
                        >
                          <span className={`h-1.5 w-1.5 rounded-full ${isOnline ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                          {isOnline ? "Live" : "Offline"}
                        </span>
                      </div>

                      {/* Column 3: Playlist (live — based on schedule + current time) */}
                      <div className="hidden md:flex items-center gap-2 min-w-0 text-sm">
                        <ListMusic className={`h-3.5 w-3.5 shrink-0 ${activePl ? "text-emerald-500" : "text-slate-400"}`} />
                        <div className="min-w-0">
                          {activePl ? (
                            <>
                              <div className="text-slate-900 truncate font-medium flex items-center gap-1.5">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                {activePl.name}
                              </div>
                              <div className="text-[11px] text-slate-500">Playing now · {activePl.media_ids.length} items</div>
                            </>
                          ) : (tv.schedules?.length || 0) > 0 ? (
                            <>
                              <div className="text-slate-500 truncate">Idle</div>
                              <div className="text-[11px] text-slate-400 italic">Outside schedule window</div>
                            </>
                          ) : (
                            <span className="text-slate-400 italic">Unassigned</span>
                          )}
                        </div>
                      </div>

                      {/* Column 4: Schedule */}
                      <div className="hidden md:block text-sm">
                        {(tv.schedules?.length || 0) > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                            <span className="text-slate-700">
                              {tv.schedules.length} slot{tv.schedules.length > 1 ? "s" : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">No schedule</span>
                        )}
                      </div>

                      {/* Column 5: Credentials hint */}
                      <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-500">
                        <KeyRound className="h-3.5 w-3.5" />
                        <span className="font-mono">{tv.username ? `@${tv.username}` : "—"}</span>
                      </div>

                      {/* Column 6: Chevron */}
                      <div className="flex justify-end">
                        <ChevronDown
                          className={`h-5 w-5 text-slate-400 transition-transform duration-300 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      </div>
                    </div>

                    {/* Expanded panel with actions + playlist picker */}
                    {isExpanded && (
                      <div
                        className="px-8 pb-7 pt-2 border-t border-slate-100/80"
                        data-testid={`tv-${tv.id}-expanded`}
                      >
                        {/* Playlist row */}
                        <div className="flex items-center gap-4 mt-3">
                          <div className="text-[11px] uppercase tracking-widest text-slate-400 font-medium whitespace-nowrap">
                            Default playlist
                          </div>
                          <div className="flex-1 max-w-md">
                            <Select
                              value={tv.playlist_id || ""}
                              onValueChange={(v) => assignPlaylist(tv.id, v)}
                            >
                              <SelectTrigger
                                className="h-9 text-sm border-slate-200 bg-white shadow-none hover:border-slate-300 transition-colors"
                                data-testid={`tv-${tv.id}-assign-playlist`}
                              >
                                <SelectValue placeholder="Select a playlist…" />
                              </SelectTrigger>
                              <SelectContent>
                                {playlists.length === 0 && (
                                  <div className="px-3 py-2 text-xs text-slate-500">No playlists yet</div>
                                )}
                                {playlists.map((p) => (
                                  <SelectItem
                                    key={p.id}
                                    value={p.id}
                                    data-testid={`tv-${tv.id}-playlist-option-${p.id}`}
                                  >
                                    {p.name} · {p.media_ids.length} items
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Action toolbar — always one horizontal row, scrolls if too narrow */}
                        <div className="mt-5 flex items-center gap-2 flex-nowrap overflow-x-auto pb-1 [scrollbar-width:thin]" data-testid={`tv-${tv.id}-toolbar`}>
                          <button
                            type="button"
                            disabled={!currentPl || currentPl.media_ids.length === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!currentPl) return;
                              setPreviewPlaylist(currentPl);
                              setPreviewTitle(`${tv.name} · ${currentPl.name}`);
                            }}
                            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-gradient-to-b from-blue-500 to-blue-600 text-white text-sm font-medium shadow-sm shadow-blue-500/30 hover:from-blue-600 hover:to-blue-700 disabled:from-slate-200 disabled:to-slate-200 disabled:text-slate-400 disabled:shadow-none disabled:cursor-not-allowed transition-all"
                            data-testid={`tv-${tv.id}-play-preview`}
                          >
                            <Play className="h-3.5 w-3.5 fill-current" /> Play preview
                          </button>

                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setQuickContentTv(tv); }}
                            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-gradient-to-b from-red-500 to-red-600 text-white text-sm font-medium shadow-sm shadow-red-500/30 hover:from-red-600 hover:to-red-700 transition-all"
                            data-testid={`tv-${tv.id}-quick-content-button`}
                          >
                            <Upload className="h-3.5 w-3.5" /> Subir contenido
                          </button>

                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); setCreatePlForTv(tv); setCreatePlOpen(true); }}
                            icon={Plus}
                            label="New playlist"
                            tone="purple"
                            testid={`tv-${tv.id}-create-playlist-button`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); setScheduleTv(tv); }}
                            icon={Clock}
                            label="Schedule"
                            tone="amber"
                            testid={`tv-${tv.id}-schedule-button`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); setCredsTv(tv); }}
                            icon={KeyRound}
                            label="Credentials"
                            tone="emerald"
                            testid={`tv-${tv.id}-show-credentials`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); refreshTv(tv.id, tv.name); }}
                            icon={RefreshCw}
                            label="Refresh"
                            tone="blue"
                            testid={`tv-${tv.id}-refresh`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); clearTvCache(tv.id, tv.name); }}
                            icon={Trash2}
                            label="Clear cache"
                            tone="amber"
                            testid={`tv-${tv.id}-clear-cache`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); setMovingDevice(tv); }}
                            icon={ArrowRightLeft}
                            label="Move account"
                            tone="purple"
                            testid={`tv-${tv.id}-move`}
                          />
                          <ToolbarButton
                            onClick={(e) => { e.stopPropagation(); signOutTv(tv.id); }}
                            icon={LogOut}
                            label="Sign out"
                            tone="slate"
                            testid={`tv-${tv.id}-sign-out`}
                          />

                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); deleteTv(tv.id); }}
                            className="shrink-0 ml-auto inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                            data-testid={`tv-${tv.id}-delete-button`}
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Remove TV
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}


      {/* New TV dialog */}
      <NewTVDialog
        open={newTvOpen}
        onOpenChange={setNewTvOpen}
        business={activeBusiness}
        onCreated={load}
      />

      {/* Create Playlist dialog */}
      <CreatePlaylistDialog
        open={createPlOpen}
        onOpenChange={setCreatePlOpen}
        tv={createPlForTv}
        business={activeBusiness}
        media={media}
        onCreated={load}
      />

      {/* Creds dialog */}
      <CredentialsDialog
        tv={credsTv}
        onClose={() => setCredsTv(null)}
        onSaved={load}
      />

      {/* Schedule dialog */}
      <ScheduleDialog
        tv={scheduleTv}
        playlists={playlists}
        onClose={() => setScheduleTv(null)}
        onChanged={load}
      />

      {/* Quick content per-device upload + schedule */}
      <QuickContentDialog
        tv={quickContentTv}
        businessId={activeBusiness?.id}
        onClose={() => setQuickContentTv(null)}
        onChanged={load}
      />

      {/* Playlist preview */}
      <PlaylistPreviewDialog
        open={!!previewPlaylist}
        onClose={() => setPreviewPlaylist(null)}
        playlist={previewPlaylist}
        media={media}
        title={previewTitle}
      />

      {/* Claim pending device dialog */}
      <ClaimDeviceDialog
        device={claimingDevice}
        businesses={businesses || []}
        playlists={playlists}
        activeBusinessId={activeBusiness?.id}
        onClose={() => setClaimingDevice(null)}
        onClaimed={() => { setClaimingDevice(null); load(); }}
      />

      {/* Move device to another business */}
      <MoveDeviceDialog
        tv={movingDevice}
        businesses={businesses || []}
        activeBusinessId={activeBusiness?.id}
        onClose={() => setMovingDevice(null)}
        onMoved={() => { setMovingDevice(null); load(); }}
      />

      <RenameTVDialog
        tv={renamingTv}
        onClose={() => setRenamingTv(null)}
        onSaved={() => { setRenamingTv(null); load(); }}
      />
    </div>
  );
}

function ClaimDeviceDialog({ device, businesses, playlists, activeBusinessId, onClose, onClaimed }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [playlistId, setPlaylistId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (device) {
      setName(""); setLocation(device.location || "");
      setBusinessId(activeBusinessId || (businesses[0]?.id || ""));
      setPlaylistId("");
    }
  }, [device, activeBusinessId, businesses]);
  if (!device) return null;

  const submit = async () => {
    if (!name.trim()) { toast.error("Give the device a name"); return; }
    if (!businessId) { toast.error("Pick an account"); return; }
    setSaving(true);
    try {
      await apiAdmin.post(`/devices/${device.id}/claim`, {
        name: name.trim(),
        location: location.trim(),
        business_id: businessId,
        playlist_id: playlistId || null,
      });
      toast.success(`Device claimed as “${name.trim()}”`);
      onClaimed();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const bizPlaylists = playlists.filter((p) => p.business_id === businessId);

  return (
    <Dialog open={!!device} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="claim-device-dialog">
        <DialogHeader>
          <DialogTitle>Claim device</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Assign this Fire Stick to an account and give it a friendly name.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-slate-50 p-2.5 text-[11px] font-mono text-slate-600 break-all">
            uid: {device.device_uid}
          </div>
          <div>
            <Label className="text-xs">Display name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Bar TV" autoFocus data-testid="claim-name-input" />
          </div>
          <div>
            <Label className="text-xs">Location (optional)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Behind the counter" data-testid="claim-location-input" />
          </div>
          <div>
            <Label className="text-xs">Account / Business *</Label>
            <Select value={businessId} onValueChange={setBusinessId}>
              <SelectTrigger className="h-10 mt-1" data-testid="claim-business-select"><SelectValue placeholder="Pick a business…" /></SelectTrigger>
              <SelectContent>
                {businesses.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Playlist (optional)</Label>
            <Select value={playlistId || "__none__"} onValueChange={(v) => setPlaylistId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-10 mt-1" data-testid="claim-playlist-select"><SelectValue placeholder="No playlist yet" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No playlist yet</SelectItem>
                {bizPlaylists.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}{p.always_on ? " · 24/7" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="claim-submit">
            {saving ? "Claiming…" : "Claim device"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDeviceDialog({ tv, businesses, activeBusinessId, onClose, onMoved }) {
  const [businessId, setBusinessId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (tv) {
      // default to the FIRST business that is NOT the current one
      const others = businesses.filter((b) => b.id !== tv.business_id);
      setBusinessId(others[0]?.id || "");
    }
  }, [tv, businesses]);
  if (!tv) return null;
  const others = businesses.filter((b) => b.id !== tv.business_id);

  const submit = async () => {
    if (!businessId) { toast.error("Pick a target account"); return; }
    setSaving(true);
    try {
      await apiAdmin.post(`/devices/${tv.id}/move`, { business_id: businessId });
      toast.success(`Moved "${tv.name}" to the new account`);
      onMoved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!tv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="move-device-dialog">
        <DialogHeader>
          <DialogTitle>Move device to another account</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            "{tv.name}" will move out of {businesses.find((b) => b.id === tv.business_id)?.name || "this account"}.
            Its current playlist &amp; schedules will be cleared — the new account starts fresh.
          </DialogDescription>
        </DialogHeader>
        {others.length === 0 ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            You need at least one other business to move this device. Create another business first.
          </div>
        ) : (
          <div className="space-y-3">
            <Label className="text-xs">Target account</Label>
            <Select value={businessId} onValueChange={setBusinessId}>
              <SelectTrigger className="h-10" data-testid="move-business-select"><SelectValue placeholder="Pick a business…" /></SelectTrigger>
              <SelectContent>
                {others.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || others.length === 0} className="bg-purple-600 hover:bg-purple-700" data-testid="move-submit">
            {saving ? "Moving…" : "Move device"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CredRow({ label, value, right }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center justify-between gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="font-mono text-sm text-slate-900 mt-0.5">{value}</div>
      </div>
      <div className="flex items-center gap-1">
        {right}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(String(value || ""));
            toast.success("Copied");
          }}
          data-testid={`credentials-copy-${label.toLowerCase()}`}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function NewTVDialog({ open, onOpenChange, business, onCreated }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const submit = async () => {
    if (!name.trim() || !location.trim()) {
      toast.error("Name and location are required");
      return;
    }
    try {
      await apiAdmin.post("/tvs", {
        name: name.trim(),
        location: location.trim(),
        business_id: business.id,
        username: username.trim() || undefined,
        password: password.trim() || undefined,
      });
      toast.success("TV added");
      onOpenChange(false);
      setName(""); setLocation(""); setUsername(""); setPassword("");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tv-add-dialog">
        <DialogHeader>
          <DialogTitle>Add a new television</DialogTitle>
          <DialogDescription>Provision a Fire Stick device for {business?.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Field label="Name" value={name} onChange={setName} placeholder="TV 6 - Rooftop" testid="tv-add-name" />
          <Field label="Location" value={location} onChange={setLocation} placeholder="Rooftop terrace" testid="tv-add-location" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username (optional)" value={username} onChange={setUsername} placeholder="auto-generated" testid="tv-add-username" />
            <Field label="Password (optional)" value={password} onChange={setPassword} placeholder="auto-generated" testid="tv-add-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tv-add-cancel">
            Cancel
          </Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700" data-testid="tv-add-submit">
            Add TV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}

function CreatePlaylistDialog({ open, onOpenChange, tv, business, media, onCreated }) {
  const [name, setName] = useState("");
  const [duration, setDuration] = useState(8);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (open) {
      setName(tv ? `${tv.location} · Loop` : "");
      setSelected([]);
      setDuration(8);
    }
  }, [open, tv]);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Playlist name required");
      return;
    }
    try {
      await apiAdmin.post("/playlists", {
        name: name.trim(),
        business_id: business.id,
        tv_id: tv?.id,
        media_ids: selected,
        item_duration: Number(duration) || 8,
      });
      toast.success("Playlist created & assigned");
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="playlist-create-dialog">
        <DialogHeader>
          <DialogTitle>Create playlist for {tv?.name}</DialogTitle>
          <DialogDescription>
            Pick the media items that should loop on this television.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs text-slate-600">Playlist name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="playlist-create-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Image duration (s)</Label>
              <Input
                type="number" min={3} max={120} value={duration} onChange={(e) => setDuration(e.target.value)}
                className="mt-1.5" data-testid="playlist-create-duration-input"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500">Media items</Label>
              <span className="text-xs text-slate-500">{selected.length} selected</span>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-80 overflow-y-auto p-1">
              {media.length === 0 && (
                <div className="col-span-full text-sm text-slate-500 text-center py-8">
                  No media uploaded yet. Add files in <b>Content Library</b>.
                </div>
              )}
              {media.map((m) => (
                <MediaThumb
                  key={m.id}
                  m={m}
                  selected={selected.includes(m.id)}
                  onToggle={() => toggle(m.id)}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="playlist-create-cancel">
            Cancel
          </Button>
          <Button onClick={submit} className="bg-blue-600 hover:bg-blue-700" data-testid="playlist-create-submit">
            Create & assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MediaThumb({ m, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`relative rounded-xl overflow-hidden border-2 ${
        selected ? "border-blue-600" : "border-transparent"
      } bg-slate-100 aspect-square text-left transition-all`}
      data-testid={`playlist-create-media-${m.id}`}
    >
      <MediaPreview m={m} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute bottom-1.5 left-2 right-2 text-[11px] text-white truncate">{m.filename}</div>
      {selected && (
        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-blue-600 text-white text-[10px] grid place-items-center font-semibold">
          ✓
        </div>
      )}
    </button>
  );
}

import { buildFileUrl as _buildFileUrl } from "@/lib/api";
function MediaPreview({ m }) {
  if (m.media_type === "image") {
    return <img src={_buildFileUrl(m.id, "admin")} alt={m.filename} className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full grid place-items-center bg-slate-900 text-white text-xs tracking-widest uppercase">
      Video
    </div>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function minutesToHHMM(m) {
  // Display in 12-hour format (e.g. "9:30 AM", "10:00 PM")
  const total = ((m % 1440) + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  const mm = String(total % 60).padStart(2, "0");
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${period}`;
}
/** Returns true iff [aStart, aEnd) overlaps [bStart, bEnd) on a day-of-week dim,
 *  handling overnight wrap (start > end means it crosses midnight).
 */
function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  const expand = (s, e) => (s <= e ? [[s, e]] : [[s, 1440], [0, e]]);
  const A = expand(aStart, aEnd);
  const B = expand(bStart, bEnd);
  for (const [s1, e1] of A) for (const [s2, e2] of B) {
    if (s1 < e2 && s2 < e1) return true;
  }
  return false;
}
/** Find existing entries that conflict with a candidate slot. */
function findConflicts(candidate, entries) {
  return entries.filter((e) => {
    if (!e.enabled) return false;
    if (e.id === candidate.id) return false;
    const sharedDays = (e.days_of_week || []).some((d) => candidate.days.includes(d));
    if (!sharedDays) return false;
    return rangesOverlap(e.start_minute, e.end_minute, candidate.start_minute, candidate.end_minute);
  });
}

function ScheduleDialog({ tv, playlists, onClose, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [playlistId, setPlaylistId] = useState("");
  // 12h-style controls: hour 1..12, minute 00..55 (step 5), period AM/PM
  const [startH, setStartH] = useState(9);  const [startM, setStartM] = useState(0);  const [startP, setStartP] = useState("AM");
  const [endH, setEndH] = useState(12);    const [endM, setEndM] = useState(0);     const [endP, setEndP] = useState("PM");
  const [days, setDays] = useState([0, 1, 2, 3, 4]); // Mon-Fri
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const open = !!tv;

  useEffect(() => {
    if (!open) return;
    setEntries(tv?.schedules || []);
    setPlaylistId(playlists[0]?.id || "");
    setStartH(9); setStartM(0); setStartP("AM");
    setEndH(12);  setEndM(0);  setEndP("PM");
    setDays([0, 1, 2, 3, 4]);
    setLabel("");
  }, [open, tv, playlists]);

  // Convert 12h state to minutes-from-midnight
  const to24 = (h, p) => (p === "AM" ? (h % 12) : (h % 12) + 12);
  const startMinutes = to24(startH, startP) * 60 + startM;
  const endMinutes = to24(endH, endP) * 60 + endM;

  // Live conflict detection — find any existing slot that overlaps with the candidate
  const conflicts = findConflicts(
    { start_minute: startMinutes, end_minute: endMinutes, days },
    entries
  );

  const toggleDay = (d) =>
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort()));

  const addEntry = async () => {
    if (!playlistId) return toast.error("Pick a playlist");
    if (!days.length) return toast.error("Pick at least one day");
    if (startMinutes === endMinutes) return toast.error("Start and end cannot be equal");
    setSaving(true);
    try {
      const { data } = await apiAdmin.post(`/tvs/${tv.id}/schedules`, {
        playlist_id: playlistId,
        start_minute: startMinutes,
        end_minute: endMinutes,
        days_of_week: days,
        enabled: true,
        label: label.trim() || undefined,
      });
      setEntries((prev) => [...prev, data]);
      setLabel("");
      onChanged();
      toast.success("Schedule slot added");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await apiAdmin.delete(`/tvs/${tv.id}/schedules/${id}`);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>Schedule for {tv?.name}</DialogTitle>
          <DialogDescription>
            Auto-switch playlists by time of day. Time uses the business timezone. Outside any schedule slot,
            the default playlist plays.
          </DialogDescription>
        </DialogHeader>

        {/* Existing entries */}
        <div className="space-y-2 mt-2">
          {entries.length === 0 && (
            <div className="text-xs text-slate-500 italic">No schedule slots yet. Add one below.</div>
          )}
          {entries.map((e) => {
            const pl = playlists.find((p) => p.id === e.playlist_id);
            return (
              <div
                key={e.id}
                className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                data-testid={`schedule-entry-${e.id}`}
              >
                <div className="text-sm">
                  <div className="font-medium text-slate-900">
                    {e.label || pl?.name || "Slot"} ·{" "}
                    <span className="font-mono">{minutesToHHMM(e.start_minute)}–{minutesToHHMM(e.end_minute)}</span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {e.days_of_week.map((d) => DAY_LABELS[d]).join(" · ")}
                    {pl && e.label ? ` · ${pl.name}` : ""}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeEntry(e.id)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`schedule-entry-${e.id}-remove`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>

        {/* New entry form */}
        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Add new slot</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-600">Playlist</Label>
              <Select value={playlistId} onValueChange={setPlaylistId}>
                <SelectTrigger className="mt-1.5" data-testid="schedule-playlist-select">
                  <SelectValue placeholder="Pick a playlist" />
                </SelectTrigger>
                <SelectContent>
                  {playlists.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name} · {p.media_ids.length} items</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start time — 12-hour picker */}
            <div>
              <Label className="text-xs text-slate-600">Start time</Label>
              <TimePicker12h
                hour={startH} minute={startM} period={startP}
                onHour={setStartH} onMinute={setStartM} onPeriod={setStartP}
                testidPrefix="schedule-start"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">End time</Label>
              <TimePicker12h
                hour={endH} minute={endM} period={endP}
                onHour={setEndH} onMinute={setEndM} onPeriod={setEndP}
                testidPrefix="schedule-end"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-600">Days of week</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {DAY_LABELS.map((d, i) => {
                  const active = days.includes(i);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(i)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`schedule-day-${i}`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-600">Label (optional)</Label>
              <Input
                value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Happy Hour, Brunch, Lunch menu"
                className="mt-1.5" data-testid="schedule-label-input"
              />
            </div>
          </div>

          {/* Conflict warning */}
          {conflicts.length > 0 && (
            <div
              className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-3"
              data-testid="schedule-conflict-warning"
            >
              <div className="flex items-start gap-2.5">
                <Clock className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" strokeWidth={2} />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-amber-900">
                    Conflict with {conflicts.length === 1 ? "existing slot" : `${conflicts.length} existing slots`}
                  </div>
                  <ul className="mt-1.5 space-y-1 text-[12px] text-amber-800">
                    {conflicts.map((c) => {
                      const pl = playlists.find((p) => p.id === c.playlist_id);
                      return (
                        <li key={c.id}>
                          • <b>{pl?.name || c.label || "Unnamed"}</b>{" "}
                          <span className="font-mono">{minutesToHHMM(c.start_minute)}–{minutesToHHMM(c.end_minute)}</span>
                          {" · "}
                          {c.days_of_week.filter((d) => days.includes(d)).map((d) => DAY_LABELS[d]).join(", ")}
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-2 text-[11px] text-amber-700">
                    💡 The first matching slot wins. Remove the conflict or pick a different time to avoid surprises.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button onClick={addEntry} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-add-button">
              <Plus className="h-4 w-4 mr-1.5" /> Add slot
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="schedule-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ToolbarButton({ icon: Icon, label, onClick, testid, tone = "slate" }) {
  const tones = {
    purple: { bg: "bg-purple-50 hover:bg-purple-100", icon: "text-purple-600", text: "text-purple-700" },
    amber:  { bg: "bg-amber-50 hover:bg-amber-100",   icon: "text-amber-600",  text: "text-amber-700"  },
    emerald:{ bg: "bg-emerald-50 hover:bg-emerald-100", icon: "text-emerald-600", text: "text-emerald-700" },
    slate:  { bg: "bg-slate-100 hover:bg-slate-200",  icon: "text-slate-600",  text: "text-slate-700"  },
    rose:   { bg: "bg-rose-50 hover:bg-rose-100",     icon: "text-rose-600",   text: "text-rose-700"   },
    blue:   { bg: "bg-blue-50 hover:bg-blue-100",     icon: "text-blue-600",   text: "text-blue-700"   },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium whitespace-nowrap ${t.bg} ${t.text} transition-colors`}
      data-testid={testid}
    >
      <Icon className={`h-3.5 w-3.5 ${t.icon}`} strokeWidth={2} /> {label}
    </button>
  );
}


function CredentialsDialog({ tv, onClose, onSaved }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [originalUsername, setOriginalUsername] = useState("");
  const [originalPassword, setOriginalPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (tv) {
      setUsername(tv.username || "");
      setPassword(tv.password_plain || "");
      setOriginalUsername(tv.username || "");
      setOriginalPassword(tv.password_plain || "");
      setShowPass(false);
    }
  }, [tv]);

  const changed = username.trim() !== originalUsername || password.trim() !== originalPassword;
  const validUsername = /^[a-z0-9_-]{3,32}$/i.test(username.trim());
  const validPassword = password.trim().length >= 4;

  const save = async () => {
    if (!tv || !changed) return;
    if (!validUsername) { toast.error("Username must be 3-32 chars (letters, numbers, _ or -)"); return; }
    if (!validPassword) { toast.error("Password must be at least 4 characters"); return; }
    setSaving(true);
    try {
      const body = {};
      if (username.trim() !== originalUsername) body.username = username.trim().toLowerCase();
      if (password.trim() !== originalPassword) body.password = password.trim();
      await apiAdmin.patch(`/tvs/${tv.id}`, body);
      toast.success("Credentials updated. The TV must log in again.");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (val, label) => {
    try {
      navigator.clipboard.writeText(val);
      toast.success(`${label} copied`);
    } catch { /* ignore */ }
  };

  return (
    <Dialog open={!!tv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="credentials-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Fire Stick credentials</DialogTitle>
          <DialogDescription>
            Edit the login for <b>{tv?.name}</b>. Saving will sign out the TV automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Username */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Username</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
                placeholder="e.g. roy-mainbar"
                className="font-mono"
                data-testid="credentials-username-input"
              />
              <button
                type="button"
                onClick={() => copyToClipboard(username, "Username")}
                className="h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors shrink-0"
                title="Copy username"
                data-testid="credentials-copy-username"
              >
                <Copy className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">3-32 characters · letters, numbers, dash or underscore</p>
          </div>

          {/* Password */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Password</Label>
            <div className="mt-1.5 flex items-center gap-2">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPass ? "text" : "password"}
                placeholder="At least 4 characters"
                className="font-mono"
                data-testid="credentials-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                className="h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors shrink-0"
                title={showPass ? "Hide" : "Show"}
                data-testid="credentials-toggle-password"
              >
                {showPass ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
              </button>
              <button
                type="button"
                onClick={() => copyToClipboard(password, "Password")}
                className="h-9 w-9 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 transition-colors shrink-0"
                title="Copy password"
                data-testid="credentials-copy-password"
              >
                <Copy className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </div>
          </div>

          {changed && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
              <KeyRound className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Saving will <b>immediately sign out</b> this Fire Stick. You will need to log in again with the new credentials.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="credentials-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={!changed || saving}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200"
            data-testid="credentials-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TimePicker12h({ hour, minute, period, onHour, onMinute, onPeriod, testidPrefix }) {
  return (
    <div className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white p-1 focus-within:border-blue-500 transition-colors">
      <Select value={String(hour)} onValueChange={(v) => onHour(Number(v))}>
        <SelectTrigger
          className="h-8 border-0 shadow-none focus:ring-0 px-2 font-mono text-sm w-[60px]"
          data-testid={`${testidPrefix}-hour`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
            <SelectItem key={h} value={String(h)}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-slate-400 font-mono select-none">:</span>
      <Select value={String(minute).padStart(2, "0")} onValueChange={(v) => onMinute(Number(v))}>
        <SelectTrigger
          className="h-8 border-0 shadow-none focus:ring-0 px-2 font-mono text-sm w-[60px]"
          data-testid={`${testidPrefix}-minute`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
            <SelectItem key={m} value={String(m).padStart(2, "0")}>{String(m).padStart(2, "0")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="flex rounded-md bg-slate-100 p-0.5 ml-auto">
        {["AM", "PM"].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPeriod(p)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded transition-colors ${
              period === p ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`${testidPrefix}-period-${p.toLowerCase()}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
